(function () {
  'use strict';

  const LEGACY_SOCIAL_TASK_TYPES = new Set(['follow_x', 'follow_ig', 'follow_ins', 'follow_link']);

  class LotteryPopup extends HTMLElement {
    connectedCallback() {
      this.config = {
        apiBase: this.getAttribute('data-api-base') || '',
        siteId: this.getAttribute('data-site-id') || 'us',
        email: this.getAttribute('data-customer-email') || '',
        isLoggedIn: this.getAttribute('data-logged-in') === 'true',
        loginUrl: this.getAttribute('data-login-url') || '/account/login',
        contactUrl: this.getAttribute('data-contact-url') || '',
        noChancesText: this.getAttribute('data-no-chances-text') || '',
        spinErrorText: this.getAttribute('data-spin-error-text') || '',
        networkErrorText: this.getAttribute('data-network-error-text') || '',
        resultCodeDesc: this.getAttribute('data-result-code-desc') || '',
        resultPhysicalDiscountDesc: this.getAttribute('data-result-physical-discount-desc') || '',
        resultPhysicalDesc: this.getAttribute('data-result-physical-desc') || '',
        copyCodeText: this.getAttribute('data-copy-code-text') || 'Copy discount code',
        resultViewProductText: this.getAttribute('data-result-view-product-text') || '',
        resultPhysicalViewProductText: this.getAttribute('data-result-physical-view-product-text') || '',
        resultContactText: this.getAttribute('data-result-contact-text') || '',
        ordersTitle: this.getAttribute('data-orders-title') || '',
        ordersEmptyText: this.getAttribute('data-orders-empty-text') || '',
        spinButtonText: this.getAttribute('data-spin-button-text') || '',
        orderButtonText: this.getAttribute('data-order-button-text') || '',
        redeemButtonText: this.getAttribute('data-redeem-button-text') || '',
        redirectButtonText: this.getAttribute('data-redirect-button-text') || '',
        doneText: this.getAttribute('data-done-text') || '',
        loadingText: this.getAttribute('data-loading-text') || '',
        noRecordsText: this.getAttribute('data-no-records-text') || '',
        taskIcons: {
          member: this.getAttribute('data-task-member-icon') || '',
          checkin: this.getAttribute('data-task-checkin-icon') || '',
          social: this.getAttribute('data-task-social-icon') || '',
        },
      };
      this.campaigns = [];
      this.activeCampaignId = null;
      this.prizes = [];
      this.tasks = [];
      this.chances = 0;
      this.claims = [];
      this.completedTasks = new Set();
      this.wheel = this.querySelector('lottery-wheel');
      this.overlay = this.querySelector('.lottery-overlay');
      this.container = this.querySelector('.lottery-container');
      this.tasksList = this.querySelector('.lottery-tasks-list');
      this.recordsList = this.querySelector('.lottery-record-list');
      this.result = this.querySelector('.lottery-result');
      this.status = this.querySelector('.lottery-status-message');
      this.loading = this.querySelector('.lottery-loading');
      this.spinButtons = this.querySelectorAll('[data-lottery-spin]');
      this.closeTimer = null;
      this.isOpening = false;
      this.defaultTheme = this.readDefaultTheme();
      this.campaignThemes = this.buildCampaignThemes();
      this.bindEvents();
      this.querySelectorAll('[data-lottery-open]').forEach((button) => button.addEventListener('click', () => this.open(button.dataset.lotteryOpen || null)));
      window.openLotteryPopup = (campaignId) => this.open(campaignId || null);
      if (new URLSearchParams(window.location.search).get('lottery') === 'open') {
        this.open();
        history.replaceState(null, '', window.location.pathname);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.wheel && typeof this.wheel.destroy === 'function') this.wheel.destroy();
    }

    bindEvents() {
      this.querySelectorAll('[data-lottery-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-result-close]').forEach((button) => button.addEventListener('click', () => this.hideResult()));
      this.querySelectorAll('[data-orders-close]').forEach((button) => button.addEventListener('click', () => this.hideOrders()));
      this.spinButtons.forEach((button) => button.addEventListener('click', () => this.draw()));
      if (this.overlay) this.overlay.addEventListener('click', (event) => { if (event.target === this.overlay) this.close(); });
      this.onKeyDown = (event) => { if (event.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this.onKeyDown);
      const copyButton = this.querySelector('.lottery-result-code .copy-icon');
      if (copyButton) copyButton.addEventListener('click', () => this.copy(this.querySelector('.code-text')?.textContent || ''));
    }

    async open(campaignId) {
      if (!this.overlay) return;
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      if (this.isOpening || this.overlay.classList.contains('is-open')) return;

      this.isOpening = true;
      this.pendingCampaignId = campaignId ? String(campaignId) : null;
      this.overlay.hidden = false;
      this.overlay.classList.add('is-open');
      this.lockScroll();
      this.showLoading(true);
      try {
        await this.checkLoginState();
        await this.loadCampaigns();
      } finally {
        this.isOpening = false;
      }
    }

    close() {
      if (!this.overlay) return;
      this.isOpening = false;
      this.overlay.classList.remove('is-open');
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        if (this.overlay) this.overlay.hidden = true;
        this.closeTimer = null;
      }, 260);
      this.unlockScroll();
    }

    lockScroll() {
      this.scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.scrollY || 0);
    }

    async checkLoginState() {
      const sectionId = this.getAttribute('data-section-id');
      if (!sectionId) return;
      try {
        const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&v=${Date.now()}`, { cache: 'no-store', credentials: 'include' });
        if (!response.ok) throw new Error(`Login state request failed (${response.status})`);
        const html = await response.text();
        const loggedIn = html.match(/data-logged-in="([^"]+)"/);
        const email = html.match(/data-customer-email="([^"]+)"/);
        this.config.isLoggedIn = Boolean(loggedIn && loggedIn[1] === 'true' && email && email[1]);
        this.config.email = this.config.isLoggedIn ? email[1] : '';
        const login = this.querySelector('.lottery-login');
        const tasks = this.querySelector('.lottery-tasks');
        const records = this.querySelector('.lottery-records');
        if (login) login.hidden = this.config.isLoggedIn;
        if (tasks) tasks.hidden = !this.config.isLoggedIn;
        if (records) records.hidden = !this.config.isLoggedIn;
      } catch (error) {
        this.config.email = '';
        this.config.isLoggedIn = false;
      }
    }

    async loadCampaigns() {
      this.showLoading(true);
      try {
        const data = await this.api('GET', '/api/lottery/campaigns');
        this.campaigns = data.campaigns || [];
        const target = this.pendingCampaignId
          ? this.campaigns.find((campaign) => String(campaign.id) === this.pendingCampaignId || String(campaign.name || '').toLowerCase() === this.pendingCampaignId.toLowerCase())
          : this.campaigns[0];
        if (!target) { this.showLoading(false); return; }
        this.activeCampaignId = target.id;
        await this.loadCampaignData();
      } catch (error) {
        this.showStatus(this.config.networkErrorText, true);
      } finally {
        this.showLoading(false);
      }
    }

    async loadCampaignData() {
      const campaignsData = await this.api('GET', '/api/lottery/campaigns');
      const campaign = (campaignsData.campaigns || []).find((item) => String(item.id) === String(this.activeCampaignId));
      this.prizes = campaign?.prizes || [];
      this.tasks = campaign?.tasks || [];
      this.applyCampaignTheme(campaign);
      const [chancesData, recordsData] = this.config.email
        ? await Promise.all([
          this.api('GET', `/api/lottery/chances?email=${encodeURIComponent(this.config.email)}`),
          this.api('GET', `/api/lottery/records?campaign_id=${encodeURIComponent(this.activeCampaignId)}&email=${encodeURIComponent(this.config.email)}`),
        ])
        : [{ chances: {} }, { records: [] }];
      const chance = (chancesData.chances || {})[this.activeCampaignId] || {};
      this.chances = Number(chance.remaining || 0);
      this.claims = (chancesData.task_claims || {})[this.activeCampaignId] || [];
      this.completedTasks = new Set(((chancesData.completed_tasks || {})[this.activeCampaignId] || []).map(String));
      await this.claimDisplayRewards();
      if (this.wheel) this.wheel.setPrizes(this.prizes.map((prize) => ({ name: prize.name, color: prize.color, displayText: prize.display_text, imageUrl: prize.image_url })));
      this.renderTasks();
      this.renderRecords(recordsData.records || []);
      this.updateChances();
    }

    readDefaultTheme() {
      return {
        wheelBg: '#006B2D',
        wheelBgImage: '',
        wheelBgImageMobile: '',
        resultIcon: '',
        wheelPointerColor: '#ff5b00',
        wheelCenterColor: '#ff5b00',
        chancesTextColor: '#858585',
        chancesStrongColor: '#ff5b00',
        outerRingColor: '#FFB476',
        dotColor1: '#ffffff',
        dotColor2: '#ffe4d6',
        textColor: '#000004',
        fontSize: 14,
        outerRingWidth: 10,
      };
    }

    buildCampaignThemes() {
      const themes = {};
      let defaultTheme = null;
      this.querySelectorAll('.lottery-hub__item').forEach((item) => {
        const campaignRef = this.normalizeCampaignRef(item.dataset.campaignRef);
        const theme = this.readThemeFromSource(item);
        if (campaignRef) themes[campaignRef] = theme;
        else if (!defaultTheme) defaultTheme = theme;
      });
      if (defaultTheme) this.defaultTheme = Object.assign({}, this.defaultTheme, defaultTheme);
      return themes;
    }

    readThemeFromSource(source) {
      const read = (name) => source.getAttribute(name) || undefined;
      const numberValue = (name) => {
        const value = read(name);
        return value === undefined ? undefined : parseFloat(value);
      };
      return {
        wheelBg: read('data-wheel-left-bg-color'),
        wheelBgImage: read('data-wheel-left-bg-image'),
        wheelBgImageMobile: read('data-wheel-left-bg-image-mobile'),
        resultIcon: read('data-result-icon'),
        wheelPointerColor: read('data-wheel-pointer-color'),
        wheelCenterColor: read('data-wheel-center-color'),
        chancesTextColor: read('data-chances-text-color'),
        chancesStrongColor: read('data-chances-strong-color'),
        outerRingColor: read('data-wheel-outer-ring-color'),
        dotColor1: read('data-wheel-dot-color1'),
        dotColor2: read('data-wheel-dot-color2'),
        textColor: read('data-wheel-text-color'),
        fontSize: numberValue('data-wheel-font-size'),
        outerRingWidth: numberValue('data-wheel-ring-width'),
      };
    }

    normalizeCampaignRef(value) { return value ? String(value).trim().toLowerCase() : ''; }

    applyCampaignTheme(campaign) {
      const theme = campaign
        ? (this.campaignThemes[this.normalizeCampaignRef(campaign.id)]
          || this.campaignThemes[this.normalizeCampaignRef(campaign.name)])
        : null;
      const nextTheme = Object.assign({}, this.defaultTheme, theme || {});
      this.activeResultIcon = nextTheme.resultIcon || '';
      const wheelBackgroundImage = this.cssImageValue(
        nextTheme.wheelBgImage,
        'none'
      );
      const wheelBackgroundImageMobile = this.cssImageValue(
        nextTheme.wheelBgImageMobile,
        wheelBackgroundImage
      );
      const cssVars = {
        '--lottery-wheel-bg': nextTheme.wheelBg,
        '--lottery-wheel-bg-image': wheelBackgroundImage,
        '--lottery-wheel-bg-image-mobile': wheelBackgroundImageMobile,
        '--lottery-wheel-pointer-color': nextTheme.wheelPointerColor,
        '--lottery-wheel-center-color': nextTheme.wheelCenterColor,
        '--lottery-chances-color': nextTheme.chancesTextColor,
        '--lottery-chances-strong-color': nextTheme.chancesStrongColor,
      };
      Object.entries(cssVars).forEach(([key, value]) => this.style.setProperty(key, value || ''));
      if (this.wheel && typeof this.wheel.setOptions === 'function') {
        this.wheel.setOptions({
          outerRingColor: nextTheme.outerRingColor,
          dotColor1: nextTheme.dotColor1,
          dotColor2: nextTheme.dotColor2,
          textColor: nextTheme.textColor,
          fontSize: nextTheme.fontSize,
          outerRingWidth: nextTheme.outerRingWidth,
        });
      }
    }

    cssImageValue(value, fallback) {
      const safeValue = String(value || '').trim().replace(/["'()\\\n\r]/g, '');
      if (!safeValue || !/^(?:https?:|\/\/|\/)/i.test(safeValue)) return fallback;
      return `url("${safeValue}")`;
    }

    taskStatus(task) {
      const currentPeriodKey = task.current_period_key;
      const claim = this.claims.find((item) => String(item.task_id) === String(task.id)
        && ['granted', 'recorded'].includes(item.status)
        && (!currentPeriodKey || item.period_key === currentPeriodKey));
      if (claim) return true;

      const periodMode = task.repeat_config?.mode || 'campaign_once';
      return ['campaign_once', 'unlimited_with_cap'].includes(periodMode)
        && this.completedTasks.has(String(task.task_type || ''));
    }

    taskSource(task) { return task.event_source || (String(task.task_type || '').startsWith('buy_') ? 'order' : String(task.task_type || '').startsWith('follow_') ? 'redirect' : task.task_type === 'info_display' ? 'display' : 'manual'); }
    taskMode(task) { return task.completion_mode || (task.task_type === 'info_display' ? 'display_only' : this.taskSource(task) === 'order' ? 'server_verified' : 'user_action'); }
    isLegacySocialTask(task, source, mode) {
      return LEGACY_SOCIAL_TASK_TYPES.has(String(task.task_type || ''))
        && source === 'redirect'
        && mode === 'user_action';
    }

    taskIconUrl(task, source) {
      const taskType = String(task.task_type || '').toLowerCase();
      if (taskType === 'member' || source === 'member') return this.config.taskIcons.member;
      if (taskType === 'checkin' || source === 'checkin') return this.config.taskIcons.checkin;
      if (source === 'redirect' || taskType.startsWith('follow_')) return this.config.taskIcons.social;
      return '';
    }

    renderTasks() {
      if (!this.tasksList) return;
      if (!this.config.isLoggedIn) { this.tasksList.innerHTML = ''; return; }
      this.tasksList.innerHTML = this.tasks.filter((task) => task.is_enabled).map((task) => {
        const source = this.taskSource(task);
        const mode = this.taskMode(task);
        const done = this.taskStatus(task);
        const isInfo = mode === 'display_only' || source === 'display' || task.task_type === 'info_display';
        const isOrder = source === 'order' || String(task.task_type || '').startsWith('buy_');
        const isLegacySocialTask = this.isLegacySocialTask(task, source, mode);
        const isAutoRedirectTask = source === 'redirect' && mode === 'user_action';
        const label = this.escape(task.name || task.label || '');
        const description = task.description ? `<div class="lottery-task-desc">${this.escape(task.description)}</div>` : '';
        const customIconUrl = task.display_config?.icon_url || task.icon_url || '';
        const iconUrl = customIconUrl || this.taskIconUrl(task, source);
        let iconClass = source === 'order' ? 'lottery-task-icon--buy' : isInfo ? 'lottery-task-icon--info' : 'lottery-task-icon--social';
        let icon = source === 'order' ? '🛒' : isInfo ? '📢' : source === 'redirect' ? '↗' : source === 'checkin' ? '✓' : '★';
        if (iconUrl) {
          iconClass += ' lottery-task-icon--image';
          icon = `<img src="${this.escape(iconUrl)}" alt="" width="48" height="48" loading="lazy">`;
        }
        const buttonText = String(task.button_text || '').trim();
        const doneText = String(task.done_text || buttonText).trim();
        let action = '';
        if (isInfo) action = description;
        else if (done && doneText) action = `<span class="lottery-task-btn lottery-task-btn--done">${this.escape(doneText)}</span>`;
        else if (isOrder && buttonText) action = `<button class="lottery-task-btn lottery-task-btn--go" type="button" data-show-orders data-task-id="${this.escape(task.id)}">${this.escape(buttonText)}</button>`;
        else if (isLegacySocialTask && buttonText) action = `<a class="lottery-task-btn lottery-task-btn--go" href="${this.escape(task.display_config?.link || task.link || '#')}" target="_blank" rel="noopener" data-social-task="${this.escape(task.task_type)}">${this.escape(buttonText)}</a>`;
        else if (isAutoRedirectTask && buttonText) action = `<a class="lottery-task-btn lottery-task-btn--go" href="${this.escape(task.display_config?.link || task.link || '#')}" target="_blank" rel="noopener" data-claim-task="${this.escape(task.id)}">${this.escape(buttonText)}</a>`;
        else if (mode === 'manual_review' && buttonText) action = `<a class="lottery-task-btn lottery-task-btn--go" href="${this.escape(task.display_config?.link || task.link || '#')}" target="_blank" rel="noopener">${this.escape(buttonText)}</a>`;
        else if (mode === 'redirect_only' && buttonText) action = `<a class="lottery-task-btn lottery-task-btn--go" href="${this.escape(task.display_config?.link || task.link || '#')}" target="_blank" rel="noopener">${this.escape(buttonText)}</a>`;
        else if (mode === 'server_verified') action = '';
        else if (buttonText) action = `<button class="lottery-task-btn lottery-task-btn--go" type="button" data-claim-task="${this.escape(task.id)}">${this.escape(buttonText)}</button>`;
        const actionHtml = action && !isInfo ? `<div class="lottery-task-action">${action}</div>` : '';
        return `<div class="lottery-task-item${isInfo ? ' lottery-task-item--info' : ''}" data-task-type="${this.escape(task.task_type || '')}"><div class="lottery-task-icon ${iconClass}">${icon}</div><div class="lottery-task-label">${label}${isInfo ? '' : description}</div>${isInfo ? '' : actionHtml}</div>`;
      }).join('');
      this.tasksList.querySelectorAll('[data-claim-task]').forEach((button) => button.addEventListener('click', (event) => {
        if (button instanceof HTMLAnchorElement) {
          event.preventDefault();
          window.open(button.href, '_blank', 'noopener,noreferrer');
        }
        this.claimTask(button.dataset.claimTask);
      }));
      this.tasksList.querySelectorAll('[data-social-task]').forEach((link) => link.addEventListener('click', () => this.completeLegacySocialTask(link.dataset.socialTask)));
      this.tasksList.querySelectorAll('[data-show-orders]').forEach((button) => button.addEventListener('click', () => this.showOrders()));
    }

    async claimDisplayRewards() {
      if (!this.config.email) return;
      const displayRewards = this.tasks.filter((task) => task.is_enabled
        && task.completion_mode === 'display_only'
        && task.reward_mode === 'fixed'
        && Number(task.reward_config?.amount || 0) > 0
        && !this.taskStatus(task));
      for (const task of displayRewards) {
        try {
          const data = await this.api('POST', `/api/lottery/tasks/${encodeURIComponent(task.id)}/claim`, {
            email: this.config.email,
            campaign_id: this.activeCampaignId,
          });
          if (!data.success) throw new Error(data.error || 'Automatic task claim failed');
          if (data.chances) this.chances = Number(data.chances.remaining ?? this.chances);
        } catch (error) {
          console.error('[Lottery] Automatic task claim error:', error);
        }
      }
    }

    async claimTask(taskId) {
      if (!this.config.email) return;
      try {
        const data = await this.api('POST', `/api/lottery/tasks/${encodeURIComponent(taskId)}/claim`, { email: this.config.email, campaign_id: this.activeCampaignId });
        if (!data.success) throw new Error(data.error || 'Task claim failed');
        await this.loadCampaignData();
      } catch (error) { this.showStatus(error.message || this.config.networkErrorText, true); }
    }

    async completeLegacySocialTask(taskType) {
      if (!this.config.email) return;
      try {
        const data = await this.api('POST', '/api/lottery/complete-task', {
          campaign_id: this.activeCampaignId,
          email: this.config.email,
          task_type: taskType,
        });
        if (!data.success) throw new Error(data.error || 'Task completion failed');
        this.completedTasks.add(String(taskType));
        this.chances = Number(data.remaining || 0);
        this.updateChances();
        this.renderTasks();
      } catch (error) { this.showStatus(error.message || this.config.networkErrorText, true); }
    }

    async showOrders() {
      if (!this.config.email || !this.activeCampaignId) return;
      const overlay = this.querySelector('.lottery-orders-overlay');
      const list = overlay?.querySelector('.lottery-orders-list');
      if (!overlay || !list) return;
      overlay.classList.add('is-visible');
      list.innerHTML = `<p>${this.escape(this.config.loadingText)}</p>`;
      try {
        const data = await this.api('GET', `/api/lottery/user-orders?email=${encodeURIComponent(this.config.email)}&campaign_id=${encodeURIComponent(this.activeCampaignId)}`);
        const orders = data.orders || [];
        list.innerHTML = orders.length ? orders.map((order) => {
          const redeemed = order.overall_status === 'redeemed' || order.overall_status === 'no_qualifying_products';
          return `<div class="lottery-order-row"><div class="lottery-order-info"><div class="lottery-order-name">${this.escape(order.order_name || order.order_id)}</div><div class="lottery-order-meta">${new Date(order.created_at).toLocaleDateString()} · ${this.escape(order.currency || '')} ${this.escape(order.total_price || '')}</div></div><div class="lottery-order-action">${redeemed ? `<span class="lottery-order-btn lottery-order-btn--done">${this.escape(this.config.doneText)}</span>` : `<button class="lottery-order-btn lottery-order-btn--redeem" type="button" data-redeem-order="${this.escape(order.order_id)}">${this.escape(this.config.redeemButtonText)}</button>`}</div></div>`;
        }).join('') : `<p>${this.escape(this.config.ordersEmptyText)}</p>`;
        list.querySelectorAll('[data-redeem-order]').forEach((button) => button.addEventListener('click', () => this.redeemOrder(button.dataset.redeemOrder, button)));
      } catch (error) { list.innerHTML = `<p>${this.escape(this.config.networkErrorText)}</p>`; }
    }

    hideOrders() { this.querySelector('.lottery-orders-overlay')?.classList.remove('is-visible'); }

    async redeemOrder(orderId, button) {
      button.disabled = true;
      try {
        const data = await this.api('POST', '/api/lottery/redeem-order', { email: this.config.email, campaign_id: this.activeCampaignId, order_name: orderId });
        if (!data.success) throw new Error(data.error || 'Redeem failed');
        button.replaceWith(Object.assign(document.createElement('span'), { className: 'lottery-order-btn lottery-order-btn--done', textContent: this.config.doneText }));
        await this.loadCampaignData();
      } catch (error) { button.disabled = false; this.showStatus(error.message || this.config.networkErrorText, true); }
    }

    async loadRecords() {
      if (!this.config.email || !this.activeCampaignId) return;
      try {
        const data = await this.api('GET', `/api/lottery/records?campaign_id=${encodeURIComponent(this.activeCampaignId)}&email=${encodeURIComponent(this.config.email)}`);
        this.renderRecords(data.records || []);
      } catch (error) {
        console.error('[Lottery] Load records error:', error);
      }
    }

    async draw() {
      if (!this.config.isLoggedIn) { window.location.href = this.config.loginUrl; return; }
      if (this.chances <= 0) { this.showStatus(this.config.noChancesText, true); return; }
      if (!this.wheel || this.wheel.isSpinning) return;
      this.spinButtons.forEach((button) => button.classList.add('is-spinning'));
      try {
        const data = await this.api('POST', '/api/lottery/draw', { email: this.config.email, campaign_id: this.activeCampaignId });
        if (!data.success) throw new Error(data.error || this.config.spinErrorText);
        const index = this.prizes.findIndex((prize) => String(prize.id) === String(data.prize?.id));
        if (index < 0) throw new Error(this.config.spinErrorText);
        await this.wheel.spin(index);
        this.chances = Math.max(0, this.chances - 1);
        this.updateChances();
        this.showResult(data.prize || {});
        await this.loadRecords();
      } catch (error) { this.showStatus(error.message || this.config.networkErrorText, true); }
      finally { this.spinButtons.forEach((button) => button.classList.remove('is-spinning')); }
    }

    showResult(prize) {
      if (!this.result) return;
      if (this.container) {
        this.container.classList.add('lottery-container--result-visible');
        this.container.scrollTop = 0;
      }
      this.result.hidden = false;
      this.result.classList.add('is-visible');
      this.result.classList.toggle('lottery-result--grand', Boolean(prize.is_grand_prize));
      const code = prize.discount_code || '';
      const codeBox = this.result.querySelector('.lottery-result-code');
      const link = this.result.querySelector('.lottery-result-link');
      const title = this.result.querySelector('.lottery-result-title');
      const desc = this.result.querySelector('.lottery-result-desc');
      const icon = this.result.querySelector('.lottery-result-icon');
      const iconEmoji = this.result.querySelector('.lottery-result-icon__emoji');
      const iconImage = this.result.querySelector('.lottery-result-icon__image');
      const contact = this.result.querySelector('.lottery-result-contact');
      if (title) title.textContent = prize.name || '';
      const prizeType = String(prize.type || '').toLowerCase();
      if (desc) {
        desc.textContent = prizeType === 'physical'
          ? this.config.resultPhysicalDesc
          : prizeType === 'physical_discount'
            ? this.config.resultPhysicalDiscountDesc
            : this.config.resultCodeDesc;
      }
      const defaultIcon = prize.is_grand_prize ? '🏆' : prize.type === 'physical' ? '🏆' : '🎉';
      if (iconEmoji && iconImage) {
        const hasResultIcon = Boolean(this.activeResultIcon);
        iconEmoji.hidden = hasResultIcon;
        iconEmoji.textContent = defaultIcon;
        iconImage.hidden = !hasResultIcon;
        if (hasResultIcon) iconImage.src = this.activeResultIcon;
        else iconImage.removeAttribute('src');
      } else if (icon) {
        icon.textContent = defaultIcon;
      }
      if (codeBox) { codeBox.hidden = !code; codeBox.querySelector('.code-text').textContent = code; codeBox.dataset.copyCode = code; }
      if (link) {
        link.hidden = !prize.product_url;
        link.href = prize.product_url || '#';
        link.textContent = prizeType === 'physical'
          ? this.config.resultPhysicalViewProductText || this.config.resultViewProductText
          : this.config.resultViewProductText;
      }
      if (contact) {
        contact.hidden = prizeType !== 'physical';
        contact.textContent = prize.contact_info || this.config.resultContactText;
        if (this.config.contactUrl) contact.href = this.config.contactUrl;
        else contact.removeAttribute('href');
      }
    }

    hideResult() {
      if (this.container) this.container.classList.remove('lottery-container--result-visible');
      if (this.result) {
        this.result.classList.remove('is-visible');
        this.result.hidden = true;
      }
    }

    copyIconMarkup() {
      return '<svg class="copy-icon" viewBox="0 0 18 17" fill="none" aria-hidden="true"><path d="M7 1H15.0328C16.1257 1 17 1.98316 17 3.21211V9.17251C17 10.4015 16.1257 11.3846 15.0328 11.3846H13.8852" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7 1C5.88889 1 5 1.90794 5 3.04288V4.46154" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M11.2933 5.61523H2.70667C1.7641 5.61523 1 6.48023 1 7.54726V14.0678C1 15.1349 1.7641 15.9998 2.70667 15.9998H11.2933C12.2359 15.9998 13 15.1349 13 14.0678V7.54726C13 6.48023 12.2359 5.61523 11.2933 5.61523Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
    }

    renderRecords(records) {
      if (!this.recordsList) return;
      this.recordsList.innerHTML = records.length ? records.map((record) => {
        const code = record.discount_code || '';
        return `<div class="lottery-record-row${record.is_grand_prize ? ' lottery-record-row--grand' : ''}"><span class="lottery-record-prize">${this.escape(record.prize_name || '')}</span>${code ? `<button class="lottery-record-code" type="button" data-copy-code="${this.escape(code)}" aria-label="${this.escape(this.config.copyCodeText)}"><span>${this.escape(code)}</span>${this.copyIconMarkup()}</button>` : ''}</div>`;
      }).join('') : `<p>${this.escape(this.config.noRecordsText)}</p>`;
      this.recordsList.querySelectorAll('[data-copy-code]').forEach((element) => element.addEventListener('click', () => this.copy(element.dataset.copyCode || '')));
    }

    updateChances() { const count = this.querySelector('.lottery-chances-count'); if (count) count.textContent = String(this.chances); }
    showLoading(visible) { if (this.loading) this.loading.classList.toggle('is-visible', visible); }
    showStatus(message, isError) { if (!this.status) return; this.status.hidden = !message; this.status.textContent = message || ''; this.status.classList.toggle('is-error', Boolean(isError)); }

    async api(method, endpoint, body) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(`${this.config.apiBase}${endpoint}`, { method, cache: 'no-store', signal: controller.signal, headers: { 'Content-Type': 'application/json', 'X-Site': this.config.siteId }, body: body ? JSON.stringify(body) : undefined });
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        return response.json();
      } catch (error) {
        if (error.name === 'AbortError') throw new Error(this.config.networkErrorText || 'Request timed out');
        throw error;
      } finally { window.clearTimeout(timer); }
    }

    copy(text) { this._copyText(text); }

    _copyText(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this._toast('Copied!');
        }).catch(() => {
          this._fallbackCopy(text);
        });
      } else {
        this._fallbackCopy(text);
      }
    }

    _fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.cssText = 'position:fixed;left:-999px;top:-999px;';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this._toast('Copied!');
      } catch (error) {}
      document.body.removeChild(textarea);
    }

    _toast(message) {
      const toast = document.createElement('div');
      toast.textContent = message;
      toast.style.cssText = 'position:fixed;top:20%;left:50%;transform:translateX(-50%);background:#333;color:#fff;padding:8px 24px;border-radius:8px;z-index:9999;font-size:14px;animation:lotteryFadeIn .2s ease;';
      document.body.appendChild(toast);
      window.setTimeout(() => toast.remove(), 1500);
    }

    escape(value) { return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])); }
  }

  if (!customElements.get('lottery-popup')) customElements.define('lottery-popup', LotteryPopup);
})();
